import { useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { format } from "date-fns";

import { Button, ButtonsWrapper } from "components/elements";
import { WarningModal } from "components/modals";
import { TiVoTargets } from "hooks/tivoApps";
import { TiVoDevicePlatform } from "hooks/tivoDevices";

import { parseLocationToQueryParams } from "../../utils";
import { TEXTS } from "../constants";
import { exportPublications, fetchPublications } from "./utils";

const FETCH_CONCURRENCY = 5;

const FETCH_LIMIT = 100;

export type PublicationsExportButtonProps = {
  platforms: TiVoDevicePlatform[];
  targets: TiVoTargets;
};

export function PublicationsExportButton({
  platforms,
  targets,
}: PublicationsExportButtonProps) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { tabId: platformId } = useParams();
  const [error, setError] = useState<string>();
  const [exporting, setExporting] = useState(false);

  const handleClick = async () => {
    if (exporting) {
      return;
    }
    try {
      setError(undefined);
      setExporting(true);
      const platform = platforms.find(({ id }) => id === platformId);
      if (!platform) {
        throw new Error(`the platform does not exist: ${platformId}`);
      }
      const queryParams = {
        ...parseLocationToQueryParams(location, FETCH_LIMIT),
        include: ["oems", "oems.clientTypes", "variant", "variant.provider"],
      };
      const publications = await fetchPublications(
        dispatch,
        platform.id,
        queryParams,
        FETCH_CONCURRENCY
      );
      const filters = Object.entries(queryParams)
        .reduce<string[]>((acc, [key, value]) => {
          if (["include", "limit", "page"].includes(key)) {
            return acc;
          }
          return acc.concat(`${key}=${value}`);
        }, [])
        .join(", ");
      const fileName =
        "publications-" +
        platform.name.replaceAll(/\s/g, "-").toLowerCase() +
        "-" +
        format(new Date(), "yyyy-LL-dd-HH-mm-ss") +
        ".csv";
      exportPublications(publications, platform, targets, filters, fileName);
    } catch (e) {
      console.error(e);
      setError(TEXTS.EXPORT_GENERIC_ERROR);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <WarningModal
        actions={
          <ButtonsWrapper position="modal">
            <Button
              type="normal"
              dataTestId={"export-publications-error-modal-close-button"}
              onClick={() => setError(undefined)}
            >
              {TEXTS.EXPORT_ERROR_MODAL_CLOSE_BUTTON_LABEL}
            </Button>
          </ButtonsWrapper>
        }
        isOpen={error}
        size="medium"
        title={TEXTS.EXPORT_ERROR_MODAL_TITLE}
        dataTestId="export-publications-error-modal"
        onClose={() => setError(undefined)}
      >
        <p>{error}</p>
      </WarningModal>
      <Button
        disabled={exporting}
        processing={exporting}
        dataTestId="export-publications-button"
        onClick={handleClick}
      >
        {TEXTS.EXPORT_BUTTON_LABEL}
      </Button>
    </>
  );
}
